import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { StateResetRegistry } from './state-reset-registry.service';

/**
 * Manages persisted filter state for the Reopenings screen.
 *
 * This service stores and restores filter selections using a BehaviorSubject
 * so component state can be preserved during navigation and reset during logout.
 *
 * Intended usage:
 * - Save filter state before navigating away from the page
 * - Restore previously applied filters when returning to the page
 * - Clear stored state during logout or explicit reset actions
 */
@Injectable({
  providedIn: 'root'
})
export class ReopeningsFiltersStateManagementService {

   private initialState = {
    selectedAudit: null,
    selectedLocation: [],
    selectedComplianceId: '',
    selectedRisks: [],
    selectedCategories: [],
    selectedAct: null,
    selectedType: [],
    selectedPeriod: null,
    startDate: null,
    endDate: null,
    selectedDepartment: null
  };

   /**
   * Observable stream exposing the current reopenings filter state.
   */
  private filterStateSubject = new BehaviorSubject<any>(this.initialState);

  filterState$ = this.filterStateSubject.asObservable();

  /**
   * Registers this service's reset with the central {@link StateResetRegistry}
   * so its filter state is cleared on logout.
   * @param stateResetRegistry - Central registry of logout reset callbacks.
   */
  constructor(private stateResetRegistry: StateResetRegistry) {
    this.stateResetRegistry.register(() => this.clearState());
  }

  /**
   * Returns the currently stored filter state.
   *
   * @returns {any} Current filter state object.
   */
  getCurrentState(): any {
    return this.filterStateSubject.getValue();
  }

   /**
   * Persists the latest reopenings filter state.
   *
   * @param {any} state Filter state object to store.
   * @returns {void}
   */
  saveState(state: any): void {
    this.filterStateSubject.next(state);
  }

  /**
   * Resets filter state back to the initial default values.
   *
   * @returns {void}
   */
  clearState(): void {
    this.filterStateSubject.next(this.initialState);
  }
}
